import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RestaurantIcon from '@mui/icons-material/Restaurant';

import type { TrendOpportunity } from '../types';
import { HEADING_SX, SECTION_HEADING_SX, TREND_COLORS, getArticleImage, getFallbackArticleImage } from '../trendTheme';
import SourceEvidencePanel from './SourceEvidencePanel';

const OPPORTUNITY_TYPE_LABELS: Record<string, string> = {
  content_gap: 'Innehållsgap',
  communication_gap: 'Kommunikationsgap',
  portfolio_gap: 'Sortimentsgap',
  strong_match: 'Stark matchning',
  underused_asset: 'Underutnyttjad tillgång',
};

type TrendOpportunityDetailDialogProps = {
  opportunity: TrendOpportunity | null;
  open: boolean;
  onClose: () => void;
};

function getLinkedArticleQualityLabel(status: TrendOpportunity['linkedArticles'][number]['qualityStatus']): string {
  if (status === 'requires_review') return 'Kräver granskning';
  if (status === 'blocked') return 'Blockerad';
  return 'Publicerbar';
}

function summarizeSourceDates(references: Array<{ publishedAt?: string | null }>) {
  const dated = references
    .map((reference) => reference.publishedAt || null)
    .filter((value): value is string => Boolean(value))
    .sort((left, right) => right.localeCompare(left));

  return {
    newest: dated[0] ? dated[0].slice(0, 10) : null,
    oldest: dated[dated.length - 1] ? dated[dated.length - 1].slice(0, 10) : null,
  };
}

export default function TrendOpportunityDetailDialog({
  opportunity,
  open,
  onClose,
}: TrendOpportunityDetailDialogProps) {
  const sourceDateSummary = summarizeSourceDates(opportunity?.sourceReferences || []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: TREND_COLORS.pageBg,
          maxHeight: '92vh',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {opportunity && (
          <Stack sx={{ px: { xs: 3, md: 4 }, py: 3 }} spacing={0}>
            {!opportunity.publicationQuality.passes && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Blockerad från publika ytor
                </Typography>
                <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }}>
                  {opportunity.publicationQuality.reasons.map((reason) => (
                    <Typography component="li" key={reason} variant="body2">
                      {reason}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Chip
                label={(OPPORTUNITY_TYPE_LABELS[opportunity.type] || opportunity.type).toUpperCase()}
                size="small"
                sx={{
                  bgcolor: TREND_COLORS.goldLight,
                  color: TREND_COLORS.terracotta,
                  fontWeight: 700,
                  fontSize: '0.62rem',
                  letterSpacing: '0.08em',
                }}
              />
              <Chip
                label={`Säkerhet ${opportunity.confidenceScore}/100`}
                size="small"
                sx={{
                  bgcolor: TREND_COLORS.oliveLight,
                  color: TREND_COLORS.olive,
                  fontWeight: 700,
                  fontSize: '0.62rem',
                  letterSpacing: '0.08em',
                }}
              />
            </Stack>

            <Typography
              sx={{
                ...HEADING_SX,
                fontSize: { xs: '1.25rem', md: '1.55rem' },
                fontWeight: 700,
                color: TREND_COLORS.navy,
                lineHeight: 1.2,
                mb: 2,
              }}
            >
              {opportunity.title}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: TREND_COLORS.textSecondary,
                lineHeight: 1.85,
                fontSize: '1.05rem',
                mb: 3,
                borderLeft: `3px solid ${TREND_COLORS.gold}`,
                pl: 2.5,
                fontStyle: 'italic',
              }}
            >
              {opportunity.summary || opportunity.whyRelevant}
            </Typography>

            <Divider sx={{ borderColor: TREND_COLORS.divider, mb: 3 }} />

            <Box sx={{ mb: 4 }}>
              <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>FÖRDJUPNING</Typography>
              <Typography
                variant="body1"
                sx={{ color: TREND_COLORS.textPrimary, lineHeight: 1.9, whiteSpace: 'pre-line' }}
              >
                {opportunity.whyRelevant || 'Ingen fördjupning tillgänglig än.'}
              </Typography>
            </Box>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ bgcolor: TREND_COLORS.cardBg, borderRadius: 2, p: 2.5, border: `1px solid ${TREND_COLORS.divider}`, height: '100%' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <StorefrontIcon sx={{ color: TREND_COLORS.olive, fontSize: 20 }} />
                    <Typography sx={{ ...HEADING_SX, fontSize: '0.85rem', fontWeight: 700, color: TREND_COLORS.navy }}>
                      DVH
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.75, whiteSpace: 'pre-line' }}>
                    {opportunity.dvhAngle || 'Ingen separat DVH-vinkel finns än.'}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ bgcolor: TREND_COLORS.cardBg, borderRadius: 2, p: 2.5, border: `1px solid ${TREND_COLORS.divider}`, height: '100%' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <RestaurantIcon sx={{ color: TREND_COLORS.terracotta, fontSize: 20 }} />
                    <Typography sx={{ ...HEADING_SX, fontSize: '0.85rem', fontWeight: 700, color: TREND_COLORS.navy }}>
                      FOODSERVICE
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.75, whiteSpace: 'pre-line' }}>
                    {opportunity.foodserviceAngle || 'Ingen separat foodservice-vinkel finns än.'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {(opportunity.linkedProducts.length > 0 || opportunity.linkedRecipes.length > 0) && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {opportunity.linkedProducts.length > 0 && (
                  <Grid size={{ xs: 12, md: opportunity.linkedRecipes.length > 0 ? 6 : 12 }}>
                    <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>PRODUKTSPÅR</Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {opportunity.linkedProducts.map((product) => (
                        <Chip
                          key={product.assetId}
                          label={product.title}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: TREND_COLORS.olive, color: TREND_COLORS.olive, fontSize: '0.68rem' }}
                        />
                      ))}
                    </Stack>
                  </Grid>
                )}
                {opportunity.linkedRecipes.length > 0 && (
                  <Grid size={{ xs: 12, md: opportunity.linkedProducts.length > 0 ? 6 : 12 }}>
                    <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>RECEPTSPÅR</Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {opportunity.linkedRecipes.map((recipe) => (
                        <Chip
                          key={recipe.assetId}
                          label={recipe.title}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: TREND_COLORS.terracotta, color: TREND_COLORS.terracotta, fontSize: '0.68rem' }}
                        />
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            )}

            {opportunity.linkedArticles?.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>UNDERLIGGANDE TRENDER</Typography>
                <Stack spacing={1.5}>
                  {opportunity.linkedArticles.map((article) => (
                    <Stack
                      key={article._id}
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{
                        bgcolor: TREND_COLORS.cardBg,
                        borderRadius: 2,
                        p: 1.5,
                        border: `1px solid ${TREND_COLORS.divider}`,
                      }}
                    >
                      <Box
                        component="img"
                        src={getArticleImage(article.heroImage, article.category)}
                        alt=""
                        onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                          const fallback = getFallbackArticleImage(article.category);
                          if (event.currentTarget.src !== fallback) event.currentTarget.src = fallback;
                        }}
                        sx={{ width: 56, height: 56, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                          <Chip
                            size="small"
                            label={getLinkedArticleQualityLabel(article.qualityStatus)}
                            sx={{ fontSize: '0.62rem', height: 18 }}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={(article.category || '').toUpperCase()}
                            sx={{ fontSize: '0.62rem', height: 18 }}
                          />
                        </Stack>
                        <Typography sx={{ ...HEADING_SX, fontSize: '0.82rem', fontWeight: 600, color: TREND_COLORS.navy }}>
                          {article.title || 'Artikel utan titel'}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            <Box>
              <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>KÄLLOR</Typography>
              <SourceEvidencePanel
                refs={opportunity.sourceEvidenceRefs}
                collectedButUnusedCount={opportunity.collectedButUnusedSourceItemIds.length}
                title="Källor bakom möjligheten"
                dense
              />
              {(sourceDateSummary.newest || sourceDateSummary.oldest) && (
                <Typography variant="caption" sx={{ display: 'block', color: TREND_COLORS.textMuted, mb: 1.5 }}>
                  {[
                    sourceDateSummary.newest ? `Senaste källa: ${sourceDateSummary.newest}` : '',
                    sourceDateSummary.oldest && sourceDateSummary.oldest !== sourceDateSummary.newest
                      ? `Äldsta källa: ${sourceDateSummary.oldest}`
                      : '',
                  ].filter(Boolean).join(' · ')}
                </Typography>
              )}
              {opportunity.sourceReferences.length > 0 ? (
                <Stack spacing={1.25} sx={{ mb: 4 }}>
                  {opportunity.sourceReferences.map((reference) => (
                    <Stack key={`${reference.title}-${reference.url}`} spacing={0.25}>
                      <Link
                        href={reference.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ color: TREND_COLORS.navy, fontWeight: 600, fontSize: '0.85rem' }}
                      >
                        {reference.title || reference.url}
                      </Link>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                        {[reference.sourceName, reference.publishedAt?.slice(0, 10), reference.note]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted, mb: 4 }}>
                  Inga källreferenser än.
                </Typography>
              )}
            </Box>

            <Box>
              <Typography sx={{ ...SECTION_HEADING_SX, mb: 2 }}>NÄSTA STEG</Typography>
              {opportunity.suggestedActions.length > 0 ? (
                <Stack spacing={1}>
                  {opportunity.suggestedActions.map((entry) => (
                    <Typography key={entry} variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.75 }}>
                      {entry}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted }}>
                  Inga rekommenderade nästa steg än.
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: TREND_COLORS.pageBg }}>
        <Button onClick={onClose} sx={{ ...HEADING_SX, fontSize: '0.8rem', color: TREND_COLORS.navy }}>
          Stäng
        </Button>
      </DialogActions>
    </Dialog>
  );
}
